import classNames from "classnames";
import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import { FiledIcon } from "components/icons/FiledIcon";
import { Inprogress } from "components/icons/InProgress";
import { NotPurchased } from "components/icons/NotPurchased";
import { OnHoldIcon } from "components/icons/JouneyTooltip/OnHoldIcon";
import {
  ACTION_PENDING,
  AMENDMENT_FILED,
  COMPLETED,
  IN_PROGRESS,
  SUCCESSFULLY_FILED,
} from "constants/taxValues";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ReactNode } from "react";
import { useDispatch } from "react-redux";
import { useAddProductsToCartAndCheckoutMutation } from "store/apis/billing";
import { taxHomePageApi } from "store/apis/taxHome";
import { BillingInvoice } from "types/Models/billing";
import { TaxTaskItem } from "types/Models/taxHome";
import { ClientProgressCycle, TaskStepDetails } from "types/Models/task";
import { BackendError } from "types/utils/error";
import { FilingCardAction } from "./FilingCardAction";
import { JourneyTooltip, STAGE_ICONS } from "./JourneyTooltip";
import { FilingActionItems } from "./FilingActionItems";
import { useLazyGetTaskByRequestIdQuery } from "store/apis/registrations";
import Card from "components/DesignSystem/Card/Card";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { CalendarWithClockIcon } from "components/icons/CalendarWithClockIcon";
import ToolTip from "components/design/toolTip";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import {
  buildClientProgressStepDetails,
  getClientStageLabel,
} from "utils/clientProgress";
import { getIsEtdHidden } from "utils/getIsEtdHidden";
import { Seperator } from "components/icons/Chat/Seperator";

export type FilingCardProps = {
  task: TaxTaskItem;
  action?: ReactNode;
  notPurchased?: boolean;
  stepDetails: TaskStepDetails[];
};

const getColor = (status: string) => {
  switch (status) {
    case ACTION_PENDING:
      return "#C67E19";
    case IN_PROGRESS:
      return "#2D7BB9";
    case SUCCESSFULLY_FILED:
    case COMPLETED:
    case AMENDMENT_FILED:
      return "#24843F";
    default:
      return "#C67E19";
  }
};

const getColors = (status: string, onlyColor?: boolean) => {
  switch (status) {
    case ACTION_PENDING:
      if (onlyColor) return "t-text-orange-80";
      return "t-bg-orange-10 t-text-orange-80";
    case IN_PROGRESS:
      if (onlyColor) return "t-text-blue-70";
      return "t-bg-blue-10 t-text-blue-70";
    case SUCCESSFULLY_FILED:
    case COMPLETED:
    case AMENDMENT_FILED:
      if (onlyColor) return "t-text-dark_green-70";
      return "t-bg-dark_green-10 t-text-dark_green-70";
    default:
      if (onlyColor) return "t-text-orange-80";
      return "t-bg-orange-10 t-text-orange-80";
  }
};

const getIcons = (status: string) => {
  switch (status) {
    case ACTION_PENDING:
      return (
        <span className="t-text-orange-80">
          <NotPurchased />
        </span>
      );
    case IN_PROGRESS:
      return (
        <span className="t-text-blue-50">
          <Inprogress />
        </span>
      );
    case SUCCESSFULLY_FILED:
    case COMPLETED:
    case AMENDMENT_FILED:
      return (
        <span className="t-text-green">
          <FiledIcon />
        </span>
      );
    default:
      return (
        <span className="t-text-orange-80">
          <NotPurchased />
        </span>
      );
  }
};

const GetCircle = ({
  percent = 0,
  status,
}: {
  percent: number;
  status?: string;
}): React.JSX.Element => {
  const size = 22;
  const strokeWidth = 3.3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(percent, 100));
  const offset = circumference * (1 - progress / 100);

  return (
    <svg width={size} height={size} className="t-block">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#D9D9D9"
        strokeWidth={strokeWidth}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={getColor(status || "")}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.35s" }}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius - strokeWidth}
        fill="#fff"
        stroke="none"
      />
    </svg>
  );
};

const OnHoldBadge = () => (
  <div className="t-bg-orange-10 t-text-orange-80 t-w-fit t-py-0.5 t-px-1.5 t-text-body-sm t-rounded-2xl t-h-[22px] t-flex t-gap-1 t-items-center">
    <div className="t-cursor-pointer -t-mt-0.5">
      <span className="t-text-orange-80">
        <OnHoldIcon />
      </span>
    </div>
    On Hold
  </div>
);

export const GetFilteredStatusSection = ({
  status,
  totalSteps,
  finishedSteps,
  stepDetails,
  isActionPending,
  showProgress = true,
  description,
  latestEtd,
  taskUuid,
  previousCycles,
}: {
  status?: string;
  totalSteps?: number;
  finishedSteps?: number;
  stepDetails: TaskStepDetails[];
  isActionPending: boolean;
  showProgress?: boolean;
  description?: string;
  latestEtd?: string | null;
  taskUuid?: string | null;
  previousCycles?: ClientProgressCycle[];
}) => {
  const visibleSteps = (stepDetails ?? []).filter(
    (s) => s.name !== "Amendment" || s.isCurrent
  );
  const isOnHold = visibleSteps.some((s) => s.isOnHold);

  if (isOnHold && showProgress) {
    return (
      <JourneyTooltip
        stepDetails={visibleSteps}
        latestEtd={latestEtd}
        taskUuid={taskUuid}
        previousCycles={previousCycles}
      >
        <div className="t-flex t-w-fit t-gap-2.5 t-items-center">
          <OnHoldBadge />
        </div>
      </JourneyTooltip>
    );
  }

  if (isOnHold) {
    return (
      <JourneyTooltip
        stepDetails={visibleSteps}
        latestEtd={latestEtd}
        taskUuid={taskUuid}
        previousCycles={previousCycles}
      >
        <OnHoldBadge />
      </JourneyTooltip>
    );
  }

  return (
    <div className="t-flex t-items-center t-gap-1.5">
      <GetStatusSection
        status={status}
        totalSteps={visibleSteps.length || totalSteps}
        finishedSteps={finishedSteps}
        stepDetails={visibleSteps}
        isActionPending={isActionPending}
        showProgress={showProgress}
        description={description}
        latestEtd={latestEtd}
        taskUuid={taskUuid}
        previousCycles={previousCycles}
      />
    </div>
  );
};

const GetStatusSection = ({
  status,
  totalSteps,
  finishedSteps,
  stepDetails,
  isActionPending,
  showProgress = true,
  description,
  latestEtd,
  taskUuid,
  previousCycles,
}: {
  status?: string;
  totalSteps?: number;
  finishedSteps?: number;
  stepDetails: TaskStepDetails[];
  isActionPending: boolean;
  showProgress?: boolean;
  description?: string;
  latestEtd?: string | null;
  taskUuid?: string | null;
  previousCycles?: ClientProgressCycle[];
}) => {
  const updatedStatus = isActionPending ? ACTION_PENDING : status;

  return (
    <JourneyTooltip
      stepDetails={stepDetails}
      latestEtd={latestEtd}
      taskUuid={taskUuid}
      previousCycles={previousCycles}
    >
      <div className="t-flex t-flex-col t-w-fit t-gap-1">
        <div className="t-flex t-w-fit t-gap-2.5 t-items-center">
          <div
            className={classNames(
              `${getColors(
                updatedStatus || ""
              )} t-w-fit t-py-0.5 t-px-1.5 t-text-body-sm t-rounded-2xl t-h-[22px] t-flex t-gap-1 t-items-center`
            )}
          >
            <div className="t-cursor-pointer -t-mt-0.5">
              {" "}
              {STAGE_ICONS[stepDetails.find((s) => s.isCurrent)?.key ?? ""] ??
                getIcons(updatedStatus || "")}
            </div>
            &nbsp; {updatedStatus}
          </div>
        </div>
      </div>
    </JourneyTooltip>
  );
};

export const FilingCard = ({
  task,
  notPurchased,
  stepDetails,
}: FilingCardProps) => {
  const {
    base_task_template,
    total_task_states: totalSteps,
    completed_task_states: finishedSteps,
    current_state,
    task_uuid: taskId,
    payment_amount: paymentAmount,
    entity,
    season,
    product_content_type_id: productContentTypeId,
    is_action_pending: isActionPending,
  } = task;
  const { title, description, uuid: productId } = base_task_template || {};
  const clientProgressCurrentKey = task.has_client_progress
    ? task.client_progress?.current_key
    : undefined;
  const clientProgressLabel = clientProgressCurrentKey
    ? getClientStageLabel({
        key: clientProgressCurrentKey,
        label: task.client_progress!.current_label,
      })
    : undefined;
  const status = clientProgressLabel || current_state?.name || "Not Purchased";
  const isEtdHidden = getIsEtdHidden(clientProgressCurrentKey);

  const { uuid: groupId } = useCurrentGroupContext();
  const checkout = useModal();
  const {
    alertToast,
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();
  const dispatch = useDispatch();
  const [
    createProductInvoice,
    { isLoading: addingProduct, data: taskInvoice },
  ] = useAddProductsToCartAndCheckoutMutation();
  const actionItemModal = useModal();
  const [getTaskByRequestId] = useLazyGetTaskByRequestIdQuery();
  const { isForeignCA } = useRoleBasedView();

  const openCheckoutModal = async () => {
    if (groupId && productId) {
      try {
        const payload = {
          entity_id: entity.uuid,
          product_content_type_id: productContentTypeId,
          product_id: productId,
          quantity: 1,
          payment_amount:
            !paymentAmount || isNaN(Number(paymentAmount))
              ? 0
              : Number(paymentAmount),
          season: season,
        };

        await createProductInvoice({
          groupId: groupId,
          entityId: entity.uuid,
          productData: [payload],
        }).unwrap();

        checkout.open();
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  };

  const handleActionItemsClose = () => {
    actionItemModal.close();
    dispatch(
      taxHomePageApi.util.invalidateTags(["TAX_HOME_UPCOMING_DEADLINE"])
    );
  };

  const handlePaymentSuccess = async () => {
    const loadingtoastId = loadingToast({
      title: "Launching filings",
      message: "Your filings are being created, please wait.",
    });
    checkout.close();

    const pollForTaskData = async (): Promise<void> => {
      const maxAttempts = 10;
      let attempts = 0;

      while (attempts < maxAttempts) {
        try {
          const taskData = await getTaskByRequestId({
            groupId: groupId,
            requestId: taskInvoice?.request_id!,
            entityId: entity.uuid,
          }).unwrap();

          if (taskData && taskData.length > 0) {
            dispatch(
              taxHomePageApi.util.invalidateTags([
                "TAX_STATS",
                "TAX_HOME_UPCOMING_DEADLINE",
              ])
            );

            return;
          }
        } catch (error) {
          alertToast({
            message: (error as BackendError).data?.error?.message,
          });
        }

        attempts++;
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
    };

    try {
      await pollForTaskData();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }

    dismissToast(loadingtoastId);
    successToast({
      title: "Filings have been launched.",
    });
  };

  return (
    <>
      <Card.Root className="t-min-h-fit">
        <Card.Body className="t-w-full t-flex t-flex-col t-gap-3">
          <section className="t-flex t-flex-col t-gap-3">
            <section className="t-flex t-flex-col t-gap-1">
              <div className="t-text-body-sm t-font-medium t-text-text-100">
                {title}
              </div>
              <div className="t-text-body-sm t-text-text-60">
                {entity?.registered_name}
              </div>
              <div
                className="t-text-body-sm-lite t-text-text-30 t-line-clamp-2"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </section>
            <section className="t-flex t-items-center t-gap-1.5">
              <GetFilteredStatusSection
                status={status}
                totalSteps={totalSteps}
                finishedSteps={finishedSteps}
                stepDetails={
                  task.has_client_progress &&
                  task.client_progress?.completed_states?.length
                    ? buildClientProgressStepDetails(task.client_progress!)
                    : stepDetails
                }
                isActionPending={
                  task.has_client_progress ? false : isActionPending
                }
                description={stepDetails?.find((s) => s.isCurrent)?.description}
                latestEtd={
                  task.has_client_progress ? task.latest_etd ?? null : null
                }
                taskUuid={
                  task.has_client_progress ? task.task_uuid ?? null : null
                }
                previousCycles={task.client_progress?.previous_cycles}
              />
              {task.has_client_progress && task.latest_etd && !isEtdHidden && (
                <>
                  <Seperator />
                  <ToolTip
                    text={
                      <div className="t-space-y-0.5">
                        <div className="t-text-body-sm t-text-white t-text-left">
                          Draft expected by this date.
                        </div>
                      </div>
                    }
                  >
                    <span className="t-text-text-30 hover:t-text-text-60 t-flex t-gap-1 t-items-center">
                      <CalendarWithClockIcon />
                      <span className="t-text-body-sm">
                        {dayjs(task.latest_etd).format(DISPLAY_DATE)}
                      </span>
                    </span>
                  </ToolTip>
                </>
              )}
            </section>
            <FilingCardAction
              paymentAmount={paymentAmount}
              taskId={taskId}
              status={current_state?.name}
              notPurchased={notPurchased}
              openCheckoutModal={openCheckoutModal}
              addingProduct={addingProduct}
              openActionItems={actionItemModal.open}
              isActionPending={isActionPending}
            />
          </section>
        </Card.Body>
      </Card.Root>
      {checkout.isOpen && Boolean(taskInvoice?.invoice) && (
        <CheckoutModal
          entityId={entity.uuid}
          type="invoices"
          onInvoicePaid={handlePaymentSuccess}
          open={checkout.isOpen}
          onClose={checkout.close}
          invoices={[taskInvoice?.invoice as BillingInvoice]}
          payerUserType={isForeignCA && "FCA"}
        />
      )}
      {actionItemModal.isOpen && taskId && title && (
        <FilingActionItems
          onClose={handleActionItemsClose}
          taskName={title}
          taskId={taskId}
        />
      )}
    </>
  );
};
